import { useState } from "react";
import { Link } from "react-router";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  CHIP_DO_ESTADO,
  ROTULO_DO_ESTADO,
  corpoDaEdicao,
  dataHoraLegivel,
  formularioDoCompromisso,
  lembreteLegivel,
  useAlterarCompromisso,
  useCancelarCompromisso,
  useConcluirCompromisso,
  useRemarcarCompromisso,
  validarHorario,
  type Compromisso,
} from "../../nucleo/compromissos";
import { FormularioDeCompromisso } from "./FormularioDeCompromisso";

type Modo = "ver" | "editar" | "remarcar" | "concluir";

/** O compromisso aberto: o que é, a que se refere e o que se pode fazer com ele. */
export function DetalheDoCompromisso({
  compromisso,
  aoFechar,
  aoRemarcar,
}: {
  compromisso: Compromisso;
  aoFechar: () => void;
  /** Avisa a tela do novo horário, para o calendário acompanhar o compromisso até a outra semana. */
  aoRemarcar?: (remarcado: Compromisso) => void;
}) {
  const [modo, setModo] = useState<Modo>("ver");
  const agendado = compromisso.estado === "agendado";

  return (
    <Cartao
      titulo={compromisso.titulo}
      data-testid="detalhe-do-compromisso"
      data-estado={compromisso.estado}
      acessorio={<Chip estado={CHIP_DO_ESTADO[compromisso.estado]}>{ROTULO_DO_ESTADO[compromisso.estado]}</Chip>}
    >
      {modo === "editar" ? (
        <Edicao compromisso={compromisso} aoTerminar={() => setModo("ver")} />
      ) : (
        <>
          <p>
            {dataHoraLegivel(compromisso.inicio_em)} até {dataHoraLegivel(compromisso.fim_em)}
            {compromisso.remarcacoes > 0
              ? ` · remarcado ${compromisso.remarcacoes} ${compromisso.remarcacoes === 1 ? "vez" : "vezes"}`
              : ""}
          </p>
          <p>
            Responsável: <b>{compromisso.responsavel.nome}</b>
            {compromisso.lembrete_minutos !== null
              ? ` · lembrete: ${lembreteLegivel(compromisso.lembrete_minutos).toLowerCase()}`
              : ""}
          </p>
          {compromisso.descricao ? <p className="mt-2">{compromisso.descricao}</p> : null}
          <ul className="mt-2" data-testid="vinculos-do-compromisso">
            {compromisso.organizacao ? (
              <li>
                Cliente:{" "}
                <Link to={`/admin/cadastros/organizacoes/${compromisso.organizacao.id}`}>
                  {compromisso.organizacao.nome}
                </Link>
              </li>
            ) : null}
            {compromisso.ticket ? (
              <li>
                Chamado:{" "}
                <Link to={`/admin/chamados/${compromisso.ticket.id}`}>
                  #{compromisso.ticket.numero} — {compromisso.ticket.assunto}
                </Link>
              </li>
            ) : null}
            {compromisso.ativo ? <li>Ativo: {compromisso.ativo.nome}</li> : null}
          </ul>
          {compromisso.resultado ? (
            <p className="mt-2" data-testid="resultado-do-compromisso">
              Resultado: {compromisso.resultado}
            </p>
          ) : null}
        </>
      )}
      {agendado && modo === "ver" ? <Acoes compromisso={compromisso} aoEscolher={setModo} /> : null}
      {modo === "remarcar" ? <Remarcacao compromisso={compromisso} aoRemarcar={aoRemarcar} aoTerminar={() => setModo("ver")} /> : null}
      {modo === "concluir" ? <Conclusao compromisso={compromisso} aoTerminar={() => setModo("ver")} /> : null}
      {modo === "ver" ? (
        <Botao variante="fantasma" tamanho="pequeno" className="mt-3" onClick={aoFechar}>
          Fechar
        </Botao>
      ) : null}
    </Cartao>
  );
}

function Acoes({ compromisso, aoEscolher }: { compromisso: Compromisso; aoEscolher: (modo: Modo) => void }) {
  const cancelar = useCancelarCompromisso();
  const [confirmando, setConfirmando] = useState(false);
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        <Botao tamanho="pequeno" onClick={() => aoEscolher("concluir")}>
          Concluir
        </Botao>
        <Botao tamanho="pequeno" variante="fantasma" onClick={() => aoEscolher("remarcar")}>
          Remarcar
        </Botao>
        <Botao tamanho="pequeno" variante="fantasma" onClick={() => aoEscolher("editar")}>
          Editar
        </Botao>
        {confirmando ? (
          <>
            <Botao
              tamanho="pequeno"
              disabled={cancelar.isPending}
              onClick={() => cancelar.mutate(compromisso.id)}
              data-testid="confirmar-cancelamento"
            >
              Sim, cancelar o compromisso
            </Botao>
            <Botao tamanho="pequeno" variante="fantasma" onClick={() => setConfirmando(false)}>
              Não cancelar
            </Botao>
          </>
        ) : (
          <Botao tamanho="pequeno" variante="fantasma" onClick={() => setConfirmando(true)}>
            Cancelar o compromisso
          </Botao>
        )}
      </div>
      {cancelar.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(cancelar.error)}</Aviso> : null}
    </div>
  );
}

function Edicao({ compromisso, aoTerminar }: { compromisso: Compromisso; aoTerminar: () => void }) {
  const alterar = useAlterarCompromisso();
  return (
    <FormularioDeCompromisso
      inicial={formularioDoCompromisso(compromisso)}
      editando
      enviando={alterar.isPending}
      erroDoServidor={alterar.error}
      aoCancelar={aoTerminar}
      aoEnviar={(form) =>
        alterar.mutate({ id: compromisso.id, corpo: corpoDaEdicao(form) }, { onSuccess: aoTerminar })
      }
    />
  );
}

function Remarcacao({
  compromisso,
  aoRemarcar,
  aoTerminar,
}: {
  compromisso: Compromisso;
  aoRemarcar?: (remarcado: Compromisso) => void;
  aoTerminar: () => void;
}) {
  const base = formularioDoCompromisso(compromisso);
  const [inicio, setInicio] = useState(base.inicio);
  const [fim, setFim] = useState(base.fim);
  const [problema, setProblema] = useState<string | null>(null);
  const remarcar = useRemarcarCompromisso();

  return (
    <form
      className="mt-3"
      noValidate
      data-testid="formulario-de-remarcacao"
      onSubmit={(evento) => {
        evento.preventDefault();
        const erro = validarHorario(inicio, fim);
        setProblema(erro);
        if (erro) return;
        remarcar.mutate(
          { id: compromisso.id, inicio, fim },
          {
            onSuccess: (remarcado) => {
              aoRemarcar?.(remarcado);
              aoTerminar();
            },
          },
        );
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Campo rotulo="Novo início" type="datetime-local" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        <Campo rotulo="Novo término" type="datetime-local" value={fim} onChange={(e) => setFim(e.target.value)} />
      </div>
      {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
      {remarcar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(remarcar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao type="submit" tamanho="pequeno" disabled={remarcar.isPending} data-testid="salvar-remarcacao">
          Remarcar
        </Botao>
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoTerminar}>
          Voltar
        </Botao>
      </div>
    </form>
  );
}

function Conclusao({ compromisso, aoTerminar }: { compromisso: Compromisso; aoTerminar: () => void }) {
  const [resultado, setResultado] = useState("");
  const concluir = useConcluirCompromisso();
  return (
    <form
      className="mt-3"
      noValidate
      data-testid="formulario-de-conclusao"
      onSubmit={(evento) => {
        evento.preventDefault();
        concluir.mutate({ id: compromisso.id, resultado }, { onSuccess: aoTerminar });
      }}
    >
      <CampoLongo
        rotulo="O que aconteceu (opcional)"
        value={resultado}
        onChange={(e) => setResultado(e.target.value)}
        ajuda="Concluir um compromisso com um cliente conta como contato na saúde dele."
      />
      {concluir.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(concluir.error)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao type="submit" tamanho="pequeno" disabled={concluir.isPending} data-testid="confirmar-conclusao">
          Concluir
        </Botao>
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoTerminar}>
          Voltar
        </Botao>
      </div>
    </form>
  );
}
